import { useToastContext } from "@ttc3k/trekker";
import {
  OrgOperatorOverlayFindOneDocument,
  useOrgOperatorOverlayFindOneQuery,
  useOrgOperatorOverlayUpsertMutation,
} from "gql/generated";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { usePostHog } from "shared/hooks";

export type UseBackOfficeCrowdRiffProps = {
  operatorEntityId: string | undefined;
  organizationId: string | undefined;
};

export const useBackOfficeCrowdRiff = ({
  operatorEntityId,
  organizationId,
}: UseBackOfficeCrowdRiffProps) => {
  const { t } = useTranslation("directory", {
    keyPrefix: "OPERATOR.BACK_OFFICE.CROWDRIFF",
  });
  const { t: tCore } = useTranslation("core");
  const { toastFactory } = useToastContext();
  const { trackPostHogException } = usePostHog();

  const [value, setValue] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  // Query the overlay for this org+operator pair
  const { data: overlayData, loading: isLoading } =
    useOrgOperatorOverlayFindOneQuery({
      variables: {
        organizationId: organizationId ?? "",
        operatorId: operatorEntityId ?? "",
      },
      skip: !organizationId || !operatorEntityId,
    });

  const serverCrowdriffId =
    overlayData?.orgOperatorOverlayFindOne?.overlayPublic?.crowdriffGalleryId ?? "";

  useEffect(() => {
    setValue(serverCrowdriffId);
  }, [serverCrowdriffId]);

  const isDirty = !isLoading && value !== serverCrowdriffId;

  const [upsertOverlay] = useOrgOperatorOverlayUpsertMutation({
    refetchQueries: [OrgOperatorOverlayFindOneDocument],
  });

  const handleSave = async (crowdriffGalleryId: string) => {
    if (!operatorEntityId || !organizationId) return;
    setIsSaving(true);
    try {
      const response = await upsertOverlay({
        variables: {
          record: {
            organizationId,
            operatorId: operatorEntityId,
            overlayPublic: {
              crowdriffGalleryId: crowdriffGalleryId || undefined,
            },
          },
        },
      });
      if (response.errors?.length) {
        throw new Error(response.errors[0].message);
      }
      toastFactory.create({
        title: tCore("SUCCESS"),
        description: t("SUCCESS"),
      });
    } catch {
      trackPostHogException(new Error("Error updating CrowdRiff gallery ID"), {
        fileName: "useBackOfficeCrowdRiff.ts",
        method: "handleSave",
        message: "Error updating CrowdRiff gallery ID",
      });
      toastFactory.create({
        title: tCore("ERROR.TITLE"),
        description: t("ERROR"),
      });
    } finally {
      setIsSaving(false);
    }
  };

  return {
    value,
    setValue,
    isSaving,
    isLoading,
    isDirty,
    onSave: handleSave,
  };
};
